import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Sparkle, X, WhatsappLogo, DownloadSimple } from "@phosphor-icons/react";

// Markdown → styled elements. Defined at module scope so React sees a stable
// component type on every render (avoids no-unstable-nested-components).
const MD_COMPONENTS = {
    h1: (p) => <h1 className="font-serif text-3xl text-ink mt-8 mb-3" {...p}/>,
    h2: (p) => <h2 className="font-serif text-2xl text-ink mt-8 mb-3 pb-2 border-b border-ink/10" {...p}/>,
    h3: (p) => <h3 className="font-serif text-xl text-ink mt-6 mb-2" {...p}/>,
    p:  (p) => <p className="text-ink/80 leading-relaxed my-3" {...p}/>,
    ul: (p) => <ul className="my-3 pl-5 space-y-1.5 list-disc marker:text-gold" {...p}/>,
    ol: (p) => <ol className="my-3 pl-5 space-y-1.5 list-decimal marker:text-gold" {...p}/>,
    li: (p) => <li className="text-ink/80 leading-relaxed" {...p}/>,
    strong: (p) => <strong className="text-ink font-semibold" {...p}/>,
    em: (p) => <em className="text-ink/90 font-serif italic" {...p}/>,
    code: (p) => <code className="font-mono text-xs bg-parchment px-1 py-0.5 rounded-sm" {...p}/>,
    blockquote: (p) => <blockquote className="border-l-2 border-gold pl-4 my-4 text-ink/70 italic" {...p}/>,
};

/**
 * AI-generated study guide for a single past paper.
 * Cache-first: /api/resources/{id}/explain returns cached content if it exists,
 * otherwise streams a Claude Sonnet response (~10-15s first time).
 */
export default function PaperExplainer({ resource, open, onClose }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);   // { cached, generated_at, explanation }

    useEffect(() => {
        if (!open || !resource) return;
        setData(null);
        setError(null);
        setLoading(true);
        api.get(`/resources/${resource.id}/explain`)
            .then(r => setData(r.data))
            .catch(e => setError(e?.response?.data?.detail || "Couldn't generate explanation. Try again in a moment."))
            .finally(() => setLoading(false));
    }, [open, resource]);

    if (!resource) return null;

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-2xl overflow-y-auto bg-bone p-0"
                data-testid="paper-explainer"
            >
                {/* Header */}
                <div className="sticky top-0 z-10 bg-bone/95 backdrop-blur-sm border-b border-ink/10 px-6 py-5">
                    <SheetHeader className="text-left space-y-2">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold">
                            <Sparkle size={14} weight="fill"/> AI study guide
                        </div>
                        <SheetTitle className="font-serif text-2xl text-ink leading-tight">
                            {resource.title}
                        </SheetTitle>
                        <SheetDescription className="text-xs text-ink/60 flex flex-wrap gap-x-3 gap-y-1">
                            <span>{resource.subject_board} · {resource.subject_name}</span>
                            <span className="font-mono">{resource.subject_code}</span>
                            {resource.year && <span>· {resource.year}</span>}
                            {resource.session && <span>· {resource.session}</span>}
                        </SheetDescription>
                    </SheetHeader>
                    <button
                        onClick={onClose}
                        className="absolute top-5 right-5 p-1.5 rounded-sm hover:bg-ink/5"
                        aria-label="Close"
                        data-testid="btn-explainer-close"
                    >
                        <X size={16} className="text-ink"/>
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-6">
                    {loading && (
                        <div className="py-16 text-center" data-testid="explainer-loading">
                            <div className="inline-flex items-center gap-3 text-ink/70">
                                <span className="inline-block w-2 h-2 rounded-full bg-gold animate-pulse"></span>
                                <span className="inline-block w-2 h-2 rounded-full bg-gold animate-pulse" style={{animationDelay: "0.2s"}}></span>
                                <span className="inline-block w-2 h-2 rounded-full bg-gold animate-pulse" style={{animationDelay: "0.4s"}}></span>
                            </div>
                            <div className="mt-6 font-serif text-lg text-ink">Reading the paper…</div>
                            <p className="text-sm text-ink/60 mt-2 max-w-sm mx-auto">
                                First-time analysis takes about 10&ndash;15 seconds. We&apos;ll cache the result
                                so everyone else opens this instantly.
                            </p>
                        </div>
                    )}

                    {error && !loading && (
                        <div className="py-12 text-center border border-oxblood/20 bg-oxblood/5 rounded-sm p-6" data-testid="explainer-error">
                            <div className="font-serif text-xl text-oxblood">Couldn&apos;t generate the guide.</div>
                            <p className="text-sm text-ink/60 mt-2">{error}</p>
                            <Button
                                onClick={() => {
                                    setError(null);
                                    setLoading(true);
                                    api.get(`/resources/${resource.id}/explain`)
                                        .then(r => setData(r.data))
                                        .catch(e => setError(e?.response?.data?.detail || "Failed again."))
                                        .finally(() => setLoading(false));
                                }}
                                className="mt-4 bg-ink text-bone hover:bg-ink/90 rounded-sm"
                                data-testid="btn-explainer-retry"
                            >
                                Try again
                            </Button>
                        </div>
                    )}

                    {data && !loading && (
                        <>
                            <article className="text-ink" data-testid="explainer-content">
                                <ReactMarkdown components={MD_COMPONENTS}>
                                    {data.explanation}
                                </ReactMarkdown>
                            </article>

                            {/* Meta + CTAs */}
                            <div className="mt-10 pt-6 border-t border-ink/10 space-y-4">
                                <div className="text-[10px] uppercase tracking-widest text-ink/40 flex items-center gap-2">
                                    {data.cached ? (
                                        <>
                                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-600"/>
                                            <span>Cached · generated {new Date(data.generated_at).toLocaleDateString()}</span>
                                        </>
                                    ) : (
                                        <>
                                            <Sparkle size={12} weight="fill" className="text-gold"/>
                                            <span>Freshly generated by Claude Sonnet 4.6</span>
                                        </>
                                    )}
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    <a
                                        href={`${api.defaults.baseURL}/resources/${resource.id}/download`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="btn-explainer-download"
                                    >
                                        <Button variant="outline" className="rounded-sm">
                                            <DownloadSimple size={14} className="mr-2"/> Open paper
                                        </Button>
                                    </a>
                                    <a
                                        href={`https://wa.me/919100560075?text=${encodeURIComponent(
                                            `Hi Wibpro, I'd like tuition help with ${resource.subject_board} ${resource.subject_name} ${resource.subject_code}${resource.paper_number ? " Paper " + resource.paper_number : ""}.`
                                        )}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="btn-explainer-whatsapp"
                                    >
                                        <Button className="bg-[#25D366] hover:bg-[#128C7E] text-white rounded-sm">
                                            <WhatsappLogo size={14} weight="fill" className="mr-2"/>
                                            Get tuition for this paper
                                        </Button>
                                    </a>
                                </div>

                                <p className="text-xs text-ink/50 italic leading-relaxed">
                                    This guide is AI-generated and meant to orient you before you attempt the paper.
                                    Always cross-check with the official mark scheme.
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}
